<template>
  <div class="fulfillment-methods">
    <div class="fulfillment-methods" :class="{ 'hide-radio-btns': isHideDNFRadioButtons && !isInternationalMode }" data-allow-mismatch="children">
      <!-- Fulfillment-specific brand-aware error notification alert -->
      <Notification v-if="fulfillmentError" severity="error" class="margin-bottom-s" aria-live="assertive" role="alert" :closable="false">
        <span>{{ fulfillmentError }}</span>
      </Notification>

      <template v-if="isShownSkeleton">
        <FulfillmentSkeleton />
      </template>

      <Suspense v-else>
        <FulfillmentWrapper
          v-bind="fulfillmentConfig"
          :page="page"
          :boss-enabled="bossEnabled"
          :upc-number="productInfo.upcNumber"
          :fcc-macys-api-key="fccMacysApiKey"
          :is-international-ship="isInternationalMode"
          :free-shipping-eligible="freeShippingEligible"
          :store-in-preference-database-enabled="storeInPreferenceDatabaseEnabled"
          :is-desktop="isDesktop"
          :get-it-today-enabled="isChipFulfillmentEnabled"
          :arrives-by-date-enabled="arrivesByDateEnabled"
          :arrives-by-date-vdf-mkp-enabled="arrivesByDateVdfMkpEnabled"
          :new-places-details-api-enabled="newPlacesDetailsApiEnabled"
          :holiday-badging-enabled="holidayBadgingEnabled"
          :event-badging="eventData"
          :sdd-promo-enabled="sddPromoEnabled"
          :bops-boss-copy-update-enabled="bopsBossCopyUpdateEnabled"
          @updated:store-details="handleUpdateStoreDetails"
          @updated:zip-code="handleChipZipCodeUpdate"
          @update:selected-option="handleSetSelectedRadioValue"
          @selected-option="handleSetSelectedRadioValue"
          @current:zip-code="handleUpdateZipCode"
        />
        <template #fallback>
          <FulfillmentSkeleton />
        </template>
      </Suspense>
    </div>
  </div>
</template>

<script setup>
/* istanbul ignore file */
import { defineAsyncComponent, onMounted, unref, computed, watch, ref, nextTick } from 'vue';
import { useMounted } from '@vueuse/core';

import Skeleton from '@atomic-ui/skeleton/Skeleton';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';
import { getLowestPrice } from '~/modules/price/parsers';
import { useEnvProps } from '~/composables/useEnvProps';
import useExperiments from '~/stores/useExperimentsStore';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { setEventData, getEventData } from '~/utils/localstorage/eventBadging';
import { useAvailability } from '../composables/useAvailability';
import { useFulfilmentData } from '../composables/useFulfilmentData';
import useGetItTodayExperiments from '../composables/useGetItTodayExperiments';
import useSelectedTraitUnavailable from '~/composables/useSelectedUnavailableTrait';
import tagAvailabilityRadioChange, { fireShippingOptionChangeTag } from '../../../features/analytics/availability';
import Notification from '@atomic-ui/notification/Notification';
import { useQuantity } from '~/modules/product/composables/useQuantity';

const props = defineProps({
  isChipFulfillmentEnabled: {
    type: Boolean,
    default: true,
  },
});

const FulfillmentSkeleton = {
  name: 'FulfillmentSkeleton',
  components: { Skeleton },
  template: `
    <div>
      <Skeleton class="margin-bottom-xs" height="44px" />
      <Skeleton height="44px" />
    </div>
  `,
};

const FulfillmentWrapper = defineAsyncComponent(
  () => import(/* webpackChunkName: "FulfillmentWrapper" */ '@nuxt-common/fulfillment/app/components/fulfillment/FulfillmentWrapper'),
);

const { zipCode, selectedRadioButton, isDataLoadNeededOnMount, isDataLoaded, fulfillmentError } = useFulfilmentData();
const { quantity } = useQuantity();

// Watch and reset fulfillment error state when quantity changes
watch(
  () => quantity?.value,
  () => {
    if (fulfillmentError) {
      fulfillmentError.value = null;
    }
  },
);
const { parseAvailabilityData, resetShippingOptionUpdateStatus, pickupStore, productInfo, isZipCodeUpdated, isStoreUpdated } = useAvailability();
const { fccMacysApiKey, freeShippingCopyClarityPrice, hostlink, brand } = useEnvProps();

const isMounted = useMounted();
const { isInternationalMode } = useFeatureEligibility();
const { isDesktop } = useDeviceType();
const { killswitches } = useKillswitches();
const selectedPrice = useProductPrice();
const {
  experimentFlags: { isHideDNFRadioButtons },
} = useExperiments();

const freeShippingEligible = ref(false);
const eventData = ref(null);

const page = 'pdp';
const {
  bossEnabled,
  eventBadgingEnabled,
  sddPromoEnabled,
  arrivesByDateEnabled,
  arrivesByDateVdfMkpEnabled,
  newPlacesDetailsApiEnabled,
  storeInPreferenceDatabaseEnabled,
  bopsBossCopyUpdateEnabled,
  holidayBadgingEnabled,
} = killswitches;
const { isGetItTodayExp1Enabled, isChipFulfillmentEnabled: isChipFulfillmentExperimentEnabled } = useGetItTodayExperiments();
const { isSelectedTraitUnavailable } = useSelectedTraitUnavailable() ?? {};
const isChipFulfillmentEnabled = computed(() => props.isChipFulfillmentEnabled && isChipFulfillmentExperimentEnabled.value);
const isChipDefaultState = computed(
  () => isChipFulfillmentEnabled.value && isGetItTodayExp1Enabled.value && !productInfo.value.selectedUpcId && !isSelectedTraitUnavailable?.value,
);
const isShownSkeleton = computed(() => !isMounted.value || (!isChipDefaultState.value && isDataLoadNeededOnMount && !isDataLoaded.value));
const chipProductInfo = computed(() => ({
  ...productInfo.value,
  selectedRadioButton: isChipDefaultState.value ? '' : productInfo.value.selectedRadioButton,
  selectedUpcId: productInfo.value.selectedUpcId || '',
}));
const chipPickupStore = computed(() => (isChipDefaultState.value ? {} : pickupStore.value));
const chipShipZipCode = computed(() => (isChipDefaultState.value ? '' : zipCode.value));
const fulfillmentComponent = computed(() => (isChipFulfillmentEnabled.value ? 'ChipComponent' : 'FulfillmentComponent'));
const fulfillmentConfig = computed(
  () =>
    ({
      ChipComponent: {
        fulfillmentComponent: 'ChipComponent',
        pickupStore: chipPickupStore.value,
        productInfo: chipProductInfo.value,
        shipZipCode: chipShipZipCode.value,
        isSelectedTraitUnavailable: isSelectedTraitUnavailable?.value || false,
      },
      FulfillmentComponent: {
        fulfillmentComponent: 'FulfillmentComponent',
        pickupStore: pickupStore.value,
        productInfo: productInfo.value,
        shipZipCode: zipCode.value,
      },
    })[fulfillmentComponent.value],
);

async function fetchEventBadgingData() {
  if (eventBadgingEnabled) {
    const badgingDataFromCache = getEventData();
    if (badgingDataFromCache) {
      await nextTick();
      eventData.value = badgingDataFromCache;
    } else {
      const brandName = brand.toUpperCase();
      const url = `${hostlink}/eventBadging/eventContent${brandName}.json`;
      try {
        let response = await fetch(url);
        response = await response.json();
        await nextTick();
        eventData.value = response;
        setEventData(response);
      } catch (e) {
        console.error('Error fetching event data:', e);
      }
    }
  }
}

onMounted(() => {
  parseAvailabilityData();
});
/**
 *
 * @param {string} selectedOption
 */
function handleSetSelectedRadioValue(selectedOption) {
  selectedRadioButton.value = selectedOption;

  tagAvailabilityRadioChange({
    radioValue: selectedOption,
    productInfo: unref(productInfo),
    selectedPrice: selectedPrice.value,
    abdTreatmentEnabled: arrivesByDateEnabled,
    abdVdfMkpTreatmentEnabled: arrivesByDateVdfMkpEnabled,
    isChipFulfillmentEnabled: isChipFulfillmentEnabled.value,
  });
}

/**
 * @typedef {object} zipCodeObj
 * @property {import('vue').Ref<string>} currentZipCode
 *
 * @param {zipCodeObj} zipCodeObj - The zip code object
 */
function handleUpdateZipCode({ currentZipCode }) {
  zipCode.value = currentZipCode.value;
}

function handleChipZipCodeUpdate(currentZipCode) {
  zipCode.value = currentZipCode;
}

function handleUpdateStoreDetails({ currentStoreDetails }) {
  pickupStore.value.storeId = currentStoreDetails.myStoreId;
  pickupStore.value.name = currentStoreDetails.myStoreName;
  currentStoreDetails.actionAfterStoreUpdate?.();
}

/**
 * @param {import("../types").Ship} ship
 */
function freeShippingEligibility(ship) {
  if (['POOL', 'STR1'].includes(ship?.orderType)) {
    const productPrice = getLowestPrice(selectedPrice.value) || 0;
    return productPrice >= freeShippingCopyClarityPrice;
  }
  return false;
}

watch(productInfo, ({ ship }) => {
  freeShippingEligible.value = freeShippingEligibility(ship);
});

watch(isShownSkeleton, (newVal) => {
  if (!newVal) {
    fetchEventBadgingData();
  }
});

watch([isZipCodeUpdated, isStoreUpdated], (status) => {
  const [zipCodeStatus, storeStatus] = status;
  if (zipCodeStatus || storeStatus) {
    if (productInfo.value.selectedRadioButton)
      fireShippingOptionChangeTag({
        zipCodeStatus,
        storeStatus,
        productInfo: productInfo.value,
        abdVdfMkpTreatmentEnabled: arrivesByDateVdfMkpEnabled,
      });
    resetShippingOptionUpdateStatus();
  }
});
</script>

<style lang="scss" scoped>
.fulfillment-methods.hide-radio-btns {
  :deep() {
    input[type='radio'] {
      display: none;
    }

    .ship-radio-label,
    .pickup-radio-label {
      margin-left: rem-calc(-32);
    }

    .shipping-message {
      padding-left: rem-calc(12);
    }
  }
}
</style>
